import { useEffect, useRef, useState } from 'react';
import { viewAsProId } from '@/lib/viewAs';
import { CELL_DOOR_OFF, ownerHubOrigin, ownerHubToken } from '@/lib/cell/ownerHub';

/**
 * YOUR TEMPLATES — the pro's library (PRO-LIBRARY-1, decisions 3c3bc3b2 + 412a2d48).
 *
 * A template is a piece the pro reuses: Erica's gift certificate first. Fill the boxes, press
 * Issue, and a NEW copy is saved with the next number — the template never changes, and the
 * list of copies IS the log. Nothing is sent by us: the pro downloads the PDF or the picture and
 * sends it themselves. The number is minted by the platform (public.pro_issue_design) and never
 * typed here — the form has no box for it, and the door drops one if it ever arrived.
 *
 * The data lives under the studio, keyed by the pro (public.designs.pro_id): access ends with
 * the tenancy, rows stay. The owner sees that pieces exist, never their contents.
 *
 * Mockup: dev.cadenceadvisers.com/design/pro-gift-certificate.html
 */
// CELL (M28): the Owner Hub's origin for this build — production's OWNER_HUB_ORIGIN (lib/hosts.ts), or the AWS staging
// host on the cell build; null there = not on the AWS staging build yet, and the door says so (CELL_DOOR_OFF).
const OWNER_HUB_ORIGIN = ownerHubOrigin();
const DOOR = `${OWNER_HUB_ORIGIN}/pro/library`;

export interface LibraryField { id: string; key: string; label: string; placeholder: string }
export interface LibraryTemplate { id: string; name: string; thumbUrl: string | null; fields: LibraryField[]; issued: number }
export interface LibraryPiece {
  id: string; name: string; no: number | null; fields: Record<string, string>; issuedAt: string | null;
  previewUrl: string | null; pdfUrl: string | null; templateId: string | null;
  /** The platform-minted code, the verification page it opens, and when it was redeemed. */
  code?: string | null; verifyUrl?: string | null; redeemedAt?: string | null;
}
type RedeemReply = { ok: boolean; error?: string; unknown?: boolean; piece?: LibraryPiece };
type ListReply = { ok: boolean; error?: string; unknown?: boolean; templates?: LibraryTemplate[]; issued?: LibraryPiece[]; firstName?: string };
type IssueReply = { ok: boolean; error?: string; unknown?: boolean; piece?: LibraryPiece; html?: string; warning?: string };

async function door<T>(body: Record<string, unknown>): Promise<T> {
  if (!OWNER_HUB_ORIGIN) return { ok: false, error: CELL_DOOR_OFF } as T;
  // CELL (M28): this tab's own token — Supabase's in production, Cognito's access token on the cell build (lib/cell/ownerHub.ts).
  const at = await ownerHubToken();
  if (!at) return { ok: false, error: 'Sign in again to open your library.' } as T;
  // HUB-027: a fetch that REJECTS (offline, blocked, dropped) used to escape this helper, so the
  // caller's setBusy(false) never ran and every button stayed frozen with no word. It now comes back
  // as a reply like any other, marked `unknown` because the request may have landed before the line
  // dropped — the caller re-reads the list rather than guessing.
  let r: Response;
  try {
    r = await fetch(DOOR, {
      method: 'POST',
      headers: { Authorization: `Bearer ${at}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, unknown: true, error: 'We lost the connection before your library answered.' } as T;
  }
  const parsed = (await r.json().catch(() => null)) as T | null;
  if (parsed) return parsed;
  // HUB-026 (fix-p2): no JSON at all — a gateway's 5xx page, a cut-off body. The door may have done
  // the work before the answer was lost (a certificate issued, then a 502), so this is `unknown` like
  // a dropped connection: the caller re-reads the list instead of inviting a blind second press.
  return ({
    ok: false,
    unknown: r.status >= 500 || r.ok,
    error: 'Your library is unreachable right now — try again in a minute.',
  } as T);
}

/** A press key the door accepts (pro.library.ts ISSUE_KEY): letters, digits, dashes. */
export function newIssueKey(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `k${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`.slice(0, 40);
}

export const pad4 = (n: number) => String(n).padStart(4, '0');
const mdy = (d: Date) => d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
const shortDay = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—');

/** Sensible starting values — every one of them the pro can overwrite. */
export function defaultsFor(fields: LibraryField[], firstName: string): Record<string, string> {
  const today = new Date(); const next = new Date(today); next.setFullYear(next.getFullYear() + 1);
  const out: Record<string, string> = {};
  for (const f of fields) {
    if (f.id === 'date') out[f.key] = mdy(today);
    else if (f.id === 'exp') out[f.key] = mdy(next);
    else if (f.id === 'auth') out[f.key] = firstName;
    else out[f.key] = f.placeholder;
  }
  return out;
}

export function ProLibrary({ locationId }: { locationId: string | null }) {
  const [templates, setTemplates] = useState<LibraryTemplate[]>([]);
  const [issued, setIssued] = useState<LibraryPiece[]>([]);
  const [firstName, setFirstName] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [issuing, setIssuing] = useState<LibraryTemplate | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ piece: LibraryPiece; html: string | null } | null>(null);
  // HUB-026: the in-flight latch (set before the first await — a state flag alone lets a fast double
  // click through) and the held press key.
  const flying = useRef(false);
  const pressKey = useRef<{ sig: string; key: string } | null>(null);
  const viewing = viewAsProId();

  // Bumped to re-read the list — after an answer we never heard, the list is the truth.
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (!locationId || viewing) return;
    let live = true;
    door<ListReply>({ location_id: locationId, act: 'list' }).then((r) => {
      if (!live) return;
      if (!r.ok) { if (!reload) setNote(r.error ?? 'Could not read your library.'); setLoaded(true); return; }
      setTemplates(r.templates ?? []); setIssued(r.issued ?? []); setFirstName(r.firstName ?? ''); setLoaded(true);
    }, () => { if (live) { setNote('Could not read your library.'); setLoaded(true); } });
    return () => { live = false; };
  }, [locationId, viewing, reload]);

  // Nothing to show: no studio, an owner looking through view-as (a pro's library is theirs), or a
  // pro with no template yet — a template is placed for them; there is nothing for them to set up.
  if (!locationId || viewing) return null;
  if (loaded && !templates.length && !note) return null;

  const startIssue = (t: LibraryTemplate) => {
    setResult(null); setNote(null); setIssuing(t); setValues(defaultsFor(t.fields, firstName));
  };
  const issue = async () => {
    if (!issuing || busy || flying.current) return;
    flying.current = true;
    setBusy(true); setNote(null);
    // HUB-026 (fix-p2): ONE PRESS, ONE CERTIFICATE. The press carries a key the platform claims
    // (pro_issue_design's keyed form): the SAME key while the same certificate is asked for again
    // after an answer that never came, so the retry returns the certificate already issued instead of
    // minting a second one; a new key once an answer arrived or the values changed.
    const sig = JSON.stringify([issuing.id, values]);
    const held = pressKey.current;
    const issueKey = held && held.sig === sig ? held.key : newIssueKey();
    pressKey.current = { sig, key: issueKey };
    let r: IssueReply;
    try {
      r = await door<IssueReply>({ location_id: locationId, act: 'issue', templateId: issuing.id, fields: values, issueKey });
    } finally {
      flying.current = false;
      setBusy(false);
    }
    if (!r.unknown) pressKey.current = null;
    if (r.unknown) {
      // It may have been issued before the line dropped. Re-read the list and say so, so a retry
      // is a decision made with the list in view, not a blind second certificate. The boxes keep
      // what was typed.
      setNote(`${r.error} Check the Issued list below before you press Issue again.`);
      setReload((n) => n + 1);
      return;
    }
    if (!r.ok || !r.piece) { setNote(r.error ?? 'Could not issue that right now.'); return; }
    setIssued((rows) => [r.piece!, ...rows]);
    setTemplates((ts) => ts.map((t) => (t.id === issuing.id ? { ...t, issued: t.issued + 1 } : t)));
    setResult({ piece: r.piece, html: r.html ?? null });
    setIssuing(null);
    // The certificate stands; the door says when its picture or PDF could not be made yet.
    if (r.warning) setNote(r.warning);
  };
  /** At the counter: mark a copy used, or undo a slip. The platform owns the fence; we relay. */
  const redeem = async (piece: LibraryPiece, redeemed: boolean) => {
    if (busy) return;
    setBusy(true); setNote(null);
    let r: RedeemReply;
    try {
      r = await door<RedeemReply>({ location_id: locationId, act: 'redeem', id: piece.id, redeemed });
    } finally {
      setBusy(false);
    }
    if (r.unknown) {
      setNote(`${r.error} The list below has been re-read — check that copy, then try again if needed.`);
      setReload((n) => n + 1);
      return;
    }
    if (!r.ok || !r.piece) { setNote(r.error ?? 'Could not update that certificate.'); return; }
    setIssued((rows) => rows.map((p) => (p.id === piece.id ? { ...p, redeemedAt: r.piece!.redeemedAt ?? null } : p)));
  };
  const openToPrint = (html: string | null) => {
    if (!html) return;
    // A blob URL rather than writing into a blank window: the document carries its own
    // <base href> (the IOSW-460 lesson), so its /m/ paths resolve from a blob origin too.
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    const w = window.open(url, '_blank');
    if (!w) { setNote('Your browser blocked the print window — allow pop-ups for this site.'); return; }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  return (
    <section className="card border-l-4 border-l-image-pink" data-pro-library>
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-image-pink mb-2">Your templates</p>
      <h2 className="text-[20px] font-bold leading-tight mb-1">Pieces you issue again and again.</h2>
      <p className="text-[14px] text-image-charcoal mb-4">
        Fill in the boxes and press Issue. Each one is saved with its own number, so you always have the list of what you gave out.
      </p>
      {note && <p role="alert" className="text-[14px] text-red-700 mb-3">{note}</p>}

      <div className="flex flex-col gap-3">
        {templates.map((t) => (
          <div key={t.id} className="flex flex-wrap items-center gap-3 sm:gap-4 rounded-xl ring-1 ring-image-border p-3" data-library-template={t.id}>
            {t.thumbUrl
              ? <img src={t.thumbUrl} alt="" className="w-[118px] aspect-[3/2] object-cover rounded-md shadow-sm flex-none" />
              : <div className="w-[118px] aspect-[3/2] rounded-md bg-image-cream flex-none" />}
            <div className="min-w-0">
              <div className="font-semibold text-[16px]">{t.name}</div>
              <div className="text-[13px] text-image-charcoal">Your template · issued {t.issued}</div>
            </div>
            <button type="button" className="btn-primary w-full sm:w-auto sm:ml-auto flex-none" onClick={() => startIssue(t)} data-library-issue>
              Issue one
            </button>
          </div>
        ))}
      </div>

      {issuing && (
        <form className="mt-4 rounded-xl ring-1 ring-image-border p-4 flex flex-col gap-3" data-library-form
          onSubmit={(e) => { e.preventDefault(); void issue(); }}>
          <div className="text-[16px] font-semibold">{issuing.name}</div>
          <p className="text-[13px] text-image-charcoal">The number is given when you press Issue — it is never one you type.</p>
          {issuing.fields.map((f) => (
            <label key={f.key} className="flex flex-col gap-1 text-[13px] text-image-charcoal">
              {f.label}
              <input className="input text-[16px]" value={values[f.key] ?? ''} placeholder={f.placeholder}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} />
            </label>
          ))}
          <div className="flex gap-2 items-center">
            <button type="submit" className="btn-primary" disabled={busy} data-library-submit>{busy ? 'Issuing…' : 'Issue'}</button>
            <button type="button" className="btn-ghost" onClick={() => setIssuing(null)}>Cancel</button>
            <span className="text-[12px] text-image-charcoal ml-auto">Saved to your list the moment you press Issue. Nothing is sent.</span>
          </div>
        </form>
      )}

      {result && (
        <div className="mt-4 rounded-xl ring-1 ring-image-pink p-4" data-library-result>
          <div className="text-[16px] font-semibold mb-2">Issued · No. {pad4(result.piece.no ?? 0)}</div>
          {result.piece.previewUrl
            ? <img src={result.piece.previewUrl} alt={result.piece.name} className="w-full rounded-md shadow-sm mb-3" />
            : <p className="text-[13px] text-image-charcoal mb-3">The picture is still rendering — it will be on your list in a moment.</p>}
          {result.piece.verifyUrl && (
            <p className="text-[13px] text-image-charcoal mb-3">
              Its QR opens <a className="underline" href={result.piece.verifyUrl} target="_blank" rel="noreferrer">{result.piece.verifyUrl.replace('https://', '')}</a> — the page that proves it is real and has your Book button.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {result.piece.pdfUrl && <a className="btn-primary" href={result.piece.pdfUrl} target="_blank" rel="noreferrer">Download PDF</a>}
            {result.piece.previewUrl && <a className="btn-ghost" href={result.piece.previewUrl} target="_blank" rel="noreferrer">Open the picture</a>}
            {result.html && <button type="button" className="btn-ghost" onClick={() => openToPrint(result.html)}>Open to print</button>}
          </div>
        </div>
      )}

      {issued.length > 0 && (
        <div className="mt-5 overflow-x-auto">
          <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-image-charcoal mb-2">Issued</div>
          <table className="w-full text-[14px]" data-library-log>
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.1em] text-image-charcoal">
                <th className="py-2 pr-3 font-semibold">No.</th><th className="py-2 pr-3 font-semibold">Amount</th>
                <th className="py-2 pr-3 font-semibold">To</th><th className="py-2 pr-3 font-semibold">Issued</th><th className="py-2 pr-3 font-semibold">Status</th><th className="py-2 font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {issued.map((p) => (
                <tr key={p.id} className="border-t border-image-border">
                  <td className="py-2 pr-3 font-mono text-[13px]">{pad4(p.no ?? 0)}</td>
                  <td className="py-2 pr-3 font-semibold">{p.fields['0:amount'] ?? '—'}</td>
                  <td className="py-2 pr-3">{p.fields['0:to'] || '—'}</td>
                  <td className="py-2 pr-3">{shortDay(p.issuedAt)}</td>
                  <td className="py-2 pr-3" data-library-status>
                    {p.redeemedAt
                      ? <span>Redeemed {shortDay(p.redeemedAt)} · <button type="button" className="underline" disabled={busy} onClick={() => void redeem(p, false)}>Undo</button></span>
                      : p.code
                        ? <button type="button" className="underline" disabled={busy} onClick={() => void redeem(p, true)} data-library-redeem>Mark redeemed</button>
                        : <span className="text-image-charcoal">—</span>}
                  </td>
                  <td className="py-2">
                    {p.pdfUrl ? <a className="underline" href={p.pdfUrl} target="_blank" rel="noreferrer">PDF</a>
                      : p.previewUrl ? <a className="underline" href={p.previewUrl} target="_blank" rel="noreferrer">Picture</a> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
